import { toDate } from "@/helpers/dateUtils";
import { Button } from "@/components/ui/button";
import { UnderwritingActivity, RequestChannel, RequestType } from "@/models/underwriting";
import { RefreshCw, RotateCcw } from "lucide-react";
import { ChannelBadge, ClaimedByCell, StatusBadge, TypeBadge } from "./activityCells";
import { formatTimeAgo } from "./activityHelpers";
import { ActivityColumn, UnderwritingActivityTable } from "./UnderwritingActivityTable";

export type TypeFilter = "all" | RequestType;
export type ChannelFilter = "all" | RequestChannel;

interface RecentActivityTableProps {
  activities: UnderwritingActivity[];
  onRowClick: (row: UnderwritingActivity) => void;
  currentUserName: string | null;
  onClaim?: (activity: UnderwritingActivity) => Promise<void>;
  /** When true, the table is showing every request for the period (incl. actioned). */
  showAll?: boolean;
  /** Expand the view to show all requests for the period. */
  onViewAll?: () => void;
  /** Revert back to showing only open requests. */
  onRevert?: () => void;
}

export function RecentActivityTable({
  activities,
  onRowClick,
  currentUserName,
  onClaim,
  showAll = false,
  onViewAll,
  onRevert,
}: RecentActivityTableProps) {
  const columns: ActivityColumn[] = [
    {
      id: "submitted",
      header: "Submitted",
      cellClassName: "text-muted-foreground",
      sortValue: (a) => toDate(a.createdOn).getTime(),
      render: (a) => formatTimeAgo(a.createdOn),
    },
    {
      id: "customer",
      header: "Customer",
      render: (a) => (
        <div className="flex flex-col">
          <span className="font-medium text-slate-700">{a.customerName ?? "—"}</span>
          <span className="text-[11px] text-muted-foreground tabular-nums">{a.applicationId}</span>
        </div>
      ),
    },
    {
      id: "status",
      header: "Status",
      render: (a) => <StatusBadge status={a.status} />,
    },
    {
      id: "accountManager",
      header: "Account Manager",
      cellClassName: "text-foreground",
      render: (a) => a.agentName ?? "—",
    },
    {
      id: "resub",
      header: "Resub",
      cellClassName: "text-muted-foreground",
      render: (a) =>
        a.resubmissionCount ? (
          <span className="flex items-center gap-1 text-amber-600 font-medium">
            <RefreshCw className="size-3" />
            {a.resubmissionCount}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      id: "affiliate",
      header: "Affiliate",
      cellClassName: "text-muted-foreground",
      render: (a) => a.affiliate ?? "—",
    },
    {
      id: "type",
      header: "Type",
      render: (a) => <TypeBadge type={a.requestType} />,
    },
    {
      id: "channel",
      header: "Channel",
      render: (a) => <ChannelBadge channel={a.channel} />,
    },
    {
      id: "claimedBy",
      header: "Claimed by",
      stopRowClick: true,
      render: (a) => (
        <ClaimedByCell activity={a} currentUserName={currentUserName} onClaim={onClaim} />
      ),
    },
  ];

  const headerActions = showAll ? (
    <Button
      variant="outline"
      size="sm"
      className="gap-1.5 text-muted-foreground"
      onClick={() => onRevert?.()}
    >
      <RotateCcw className="size-3.5" />
      Show open only
    </Button>
  ) : (
    <button
      onClick={() => onViewAll?.()}
      className="text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors"
    >
      View all requests →
    </button>
  );

  return (
    <UnderwritingActivityTable
      activities={activities}
      columns={columns}
      onRowClick={onRowClick}
      title={showAll ? "All Requests" : "Open Requests"}
      headerActions={headerActions}
    />
  );
}
